(ns app.domain.application.guards
  ;; returning boolean might be more readable in some guards, disable (not (empty? x)) linter
  {:clj-kondo/config '{:linters {:not-empty? {:level :off}}}}
  (:require #?(:clj [app.domain.sign-off.core :as sign-off])
            #?(:clj [app.time :as time])
            #?(:clj [core.permission.core :as perm])
            #?(:cljs [core.front.date-util :as date-util])
            [app.calendar :as calendar]
            [app.domain.acceptance.model :as acceptance-model]
            [app.domain.application.condition.model :as application-condition-model]
            [app.domain.application.guards.appeals :as appeals-guards]
            [app.domain.application.guards.bim :as bim-guards]
            [app.domain.application.guards.bonds :as bond-guards]
            [app.domain.application.guards.inspection :as inspection-guards]
            [app.domain.application.guards.licence :as licence-guards]
            [app.domain.application.guards.party :as party-guards]
            [app.domain.application.guards.periodic-inspection :as periodic-inspection-guards]
            [app.domain.application.guards.property :as property-guards]
            [app.domain.application.guards.related :as related-guards]
            [app.domain.application.guards.reviews :as review-guards]
            [app.domain.application.guards.states :as states-guards]
            [app.domain.application.guards.utils :as guard-utils]
            [app.domain.application.model :as app-model]
            [app.domain.approval.model :as approval-model]
            [app.domain.archiving.model :as archiving-model]
            [app.domain.execution.model :as execution-model]
            [app.domain.fee.model :as fee-model]
            [app.domain.form.model :as form-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.meeting.model :as meeting-model]
            [app.domain.party.model :as party-model]
            [app.domain.phase-result.model :as phase-result-model]
            [app.domain.planning-report.model :as planning-report-model]
            [app.domain.project.model :as project-model]
            [app.domain.review.model :as review-model]
            [app.domain.sign-off.model :as sign-off.model]
            [app.domain.sign-off.sign-off-properties :as sign-off-props]
            [app.domain.user.authority :as authority]
            [app.domain.util :as du]
            [app.domain.verdict.model :as verdict-model]
            [app.domain.verdict.section :as verdict-section]
            [app.domain.workspace.model :as workspace-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [core.permission.resolver :as permission-resolver]
            [potpuri.core :as p]
            [tilakone.core :as tk]
            [tilakone.util :as tk-util]))

(defn find-workspace [fsm]
  (-> fsm :request :target :entity/parent))

(defn feature-flag-enabled? [fsm feature-flag-id]
  (contains? (-> fsm :request :feature-flags) feature-flag-id))

(defn feature-flag-enabled-handler [fsm]
  (feature-flag-enabled? fsm (-> fsm ::tk/guard second)))

(def feature-enabled?
  {:name    :process.guard/feature-enabled?
   :type    :guard
   :summary "Only allowed if the specified global feature flag is enabled"
   :handler feature-flag-enabled-handler})

(def feature-not-enabled?
  {:name    :process.guard/feature-not-enabled?
   :type    :guard
   :summary "Only allowed if the specified global feature flag is *not* enabled"
   :handler (complement feature-flag-enabled-handler)})

(def ^{:see-also ["app.process.construct" "cond-guard"]} cond-guard
  {:name    :process.guard/cond
   :type    :guard
   :summary "Works similar to Clojure's `cond`: Takes list of test/expr pairs where each test and expression is a guard.
             Finds the first test/expr pair, whose evaluated test guard is allowed, and returns evaluated result of
             related expression guard. For more information on usage, see ´app.process.construct/cond-guard´"
   :handler (fn [fsm]
              (let [clauses       (-> fsm ::tk/guard next)
                    guard-eval-fn (partial #'tk-util/try-guard fsm)]
                (some->
                  ; partition clauses to pairs: (predicate expression, value expression)
                  (partition 2 clauses)
                  ; find first pair whose pred guard allowed true
                  (p/find-first (fn [[pred-guard _val-guard]]
                                  (-> pred-guard guard-eval-fn :tilakone.core/allow?)))
                  ; take result of value guard
                  second guard-eval-fn :tilakone.core/result)))})

(def constantly-true
  {:name    :process.guard/constantly-true
   :type    :guard
   :summary "Always true"
   :handler (constantly true)})


(def constantly-false
  {:name    :process.guard/constantly-false
   :type    :guard
   :summary "Always false"
   :handler (constantly false)})

(defn target-in-state-handler [{[_ & states]     ::tk/guard
                                {:keys [target]} :request}]
  (or (contains? (set states)
                 (:process/state target))
      (contains? (set states)
                 (:domain/state target))))

(def target-in-state?
  {:name    :process.guard/target-in-state?
   :type    :guard
   :summary "Check whether the target is in a given state. This can be used to create a :next-step-guard
             that hides a specific state until it's actually reached"
   :handler target-in-state-handler})

(def target-not-in-state?
  {:name    :process.guard/target-not-in-state?
   :type    :guard
   :summary "Check whether the target is not in a given state."
   :handler (complement target-in-state-handler)})

(def application-in-state?
  {:name    :process.guard/application-in-state?
   :type    :guard
   :summary "Check whether the sibling application phase is in a given state. Lapsing overwrites the
             fulfillment phase's approved / approved-in-principle state, so the application phase
             is the only place where we can check approval status of the workspace."
   :handler (fn [{[_ & states] ::tk/guard :as fsm}]
              (contains? (set states)
                         (-> fsm
                             find-workspace
                             workspace-model/workspace-application
                             :process/state)))})

(def integration-sync-state-allow-sync?
  {:name    :process.guard/integration-sync-state-allow-sync?
   :type    :guard
   :handler (fn [fsm]
              (-> fsm find-workspace :workspace/integration-sync-state (not= :in-progress)))})

(def ecm-sync-state-allow-sync?
  {:name    :process.guard/ecm-sync-state-allow-sync?
   :type    :guard
   :handler (fn [fsm]
              (let [state (-> fsm find-workspace :workspace/ecm-sync-state)]
                (or (nil? state) (= state :workspace.ecm-sync-state/ready))))})

(def workspace-package-sent-to-ecm?
  {:name    :process.guard/workspace-package-sent-to-ecm?
   :type    :guard
   :summary "Check that at least one workspace content package has been sent to ECM system or no integration is available"
   :handler (fn [{{{workspace :entity/parent} :target} :request
                  :as                                  fsm}]
              (if (and (guard-utils/group-feature-available? fsm :group.feature/ecm-integration)
                       (config-model/resolve-boolean :group.config/ecm-integration-container-id
                                                     workspace))
                (->> (workspace-model/packages workspace)
                     (some :entity/ecm-integration-id))
                true))})

(defn child-config-exists? [config-fn fsm]
  (let [authority? (guard-utils/has-permission? fsm :permission.application/authority-submit)]
    (->> fsm
         :request
         :target
         config-fn
         config-model/child-configs
         (filter (every-pred
                   ;; if config is not selectable for all, it should not be mandatory
                   #(config-model/allow-workspace-creation? authority? true %)
                   ;; also, disabled configs are disregarded
                   (du/in-state-fn :group-config.state/enabled)))
         seq)))

(def work-types-exist?
  (partial child-config-exists?
           (comp :workspace/category-group-config
                 :entity/parent)))

(def work-target-exist?
  (partial child-config-exists?
           ;; if there are multiple work-types, it is prevented elsewhere to have any work-targets
           ;; under them, so checking first is sufficient. Do *not* use `app-model/single-work-type`
           ;; though, because it's perfectly valid to call this with a multi-work-type-permit-type.
           (comp first
                 :application/work-type-group-configs)))

(def integration-sync-work-type-set?
  {:name    :process.guard/work-type+target-set-or-not-available?
   :summary "Checks that if applicable, both work-type and work-target have been selected."
   :type    :guard
   :handler (fn [fsm]
              (let [application (-> fsm find-workspace workspace-model/workspace-application)]
                (or (not (work-types-exist? fsm))
                    (and (-> application :application/work-type-group-configs seq)
                         (or (not (work-target-exist? fsm))
                             (-> application :application/work-target-group-config))))))})


(defn- none-paid? [invoices]
  (->> invoices
       (map :domain/state)
       (not-any? #{:invoice.state/paid :bill.state/paid})))


(defn- all-paid-or-canceled? [invoices]
  (->> invoices
       (map :domain/state)
       (every? #{:invoice.state/paid :bill.state/paid :bill.state/canceled})))

(defn- bills-for-phase [application phase]
  (->> application
       fee-model/phase-bills
       (filter #(-> % :bill/phase (= phase)))))

(defn- upfront-bills [application]
  (bills-for-phase application :bill.phase/upfront))

(defn- deposit-invoices [application]
  (bills-for-phase application :bill.phase/deposit))

(def deposit-paid-or-not-required?
  {:name    :process.guard/deposit-paid-or-not-required?
   :type    :guard
   :handler (fn [fsm]
              ; This presumes that if the application does require a deposit
              ; the invoice for that must have been created
              (if-let [invoices (-> fsm :request :target deposit-invoices seq)]
                (all-paid-or-canceled? invoices)
                true))})

(def upfront-bill-paid-or-not-required?
  {:name    :process.guard/upfront-bill-paid-or-not-required?
   :type    :guard
   :handler (fn [fsm]
              ; This presumes that if the application does require an upfront payment
              ; the bill for that must have been created
              (if-let [bills (-> fsm :request :target upfront-bills seq)]
                (all-paid-or-canceled? bills)
                true))})


(defn- bills-exist-and-none-paid? [fsm bill-getter-fn]
  (let [bills (->> fsm
                   :request
                   :target
                   bill-getter-fn
                   (remove (du/in-state-fn :bill.state/canceled))
                   seq)]
    (and (not-empty bills)
         (none-paid? bills))))


(def unpaid-upfront-or-deposit-bill-created?
  {:name    :process.guard/unpaid-upfront-or-deposit-bill-created?
   :type    :guard
   :handler (fn [fsm]
              (bills-exist-and-none-paid? fsm #(concat (upfront-bills %)
                                                       (deposit-invoices %))))})


(def unpaid-pre-consultation-request-bill-created?
  {:name    :process.guard/unpaid-pre-consultation-request-bill-created?
   :type    :guard
   :handler (fn [fsm]
              (bills-exist-and-none-paid? fsm #(bills-for-phase % :bill.phase/pre-consultation-request)))})

#?(:clj
   (defn- readable-by-user [fsm]
     (perm/readable-by-fn (:core.user/user fsm))))


(def required-fields-set?
  {:name    :process.guard/required-fields-set?
   :type    :guard
   :handler (fn [fsm]
              (let [application (-> fsm :request :target)
                    forms       (->> application
                                     :entity/_parent
                                     (filter #(-> % :entity/type (= :entity.type/form)))
                                     #?(:clj (filter (readable-by-user fsm))))]
                (every? form-model/form-required-fields-set? forms)))})


(def required-fields-except-affidavit-set?
  {:name    :process.guard/required-fields-except-affidavit-set?
   :type    :guard
   :handler (fn [fsm]
              (let [application (-> fsm :request :target)
                    forms       (->> application
                                     :entity/_parent
                                     (filter #(-> % :entity/type (= :entity.type/form)))
                                     #?(:clj (filter (readable-by-user fsm))))]
                (every? #(form-model/form-required-fields-set? % #{:affidavit}) forms)))})


(def required-fields-except-sign-off-set?
  {:name    :process.guard/required-fields-except-sign-off-set?
   :type    :guard
   :handler (fn [fsm]
              (let [application (-> fsm find-workspace workspace-model/workspace-application)
                    forms       (->> application
                                     form-model/forms
                                     #?(:clj (filter (readable-by-user fsm)))
                                     (remove #(some-> application :application/sign-off-form (= %))))]
                (every? form-model/form-required-fields-set? forms)))})


(def forms-confirmed-for-submit?
  "When the form-confirmation flag is enabled, every visible form on the target phase the submitting user can write
   that AI has updated must be confirmed (:form/confirmed-at >= :form/ai-updated-at).
   Authority-only forms (lacking applicant write permission), forms hidden by field configuration,
   forms without AI updates, and sign-off forms are exempt. No-op when the flag is disabled."
  {:name    :process.guard/forms-confirmed-for-submit?
   :type    :guard
   :handler (fn [fsm]
              (or (not (feature-flag-enabled? fsm :feature-flag.id/form-confirmation))
                  (let [user  (:core.user/user fsm)
                        phase (-> fsm :request :target)
                        forms (->> phase
                                   form-model/forms
                                   #?(:clj (filter (readable-by-user fsm)))
                                   (filter form-model/get-form-data-schema))]
                    (every? #(form-model/form-confirmation-satisfied? % user) forms))))})


(def required-sign-off-fields-set?
  {:name    :process.guard/required-sign-off-fields-set?
   :type    :guard
   :handler (fn [fsm]
              (let [form (-> fsm :request :target :application/sign-off-form)]
                (form-model/form-required-fields-set? form)))})


(def attachment-requirements-are-fulfilled?
  {:name    :process.guard/attachment-requirements-are-fulfilled?
   :type    :guard
   :handler (fn [fsm]
              (let [children                  (-> fsm :request :target :entity/_parent)
                    attachment-type-present?  (->> children
                                                   (filter #(-> % :entity/type (= :entity.type/attachment)))
                                                   (mapcat :attachment/types)
                                                   set)
                    required-attachment-types (->> children
                                                   (filter #(-> % :requirement/kind (= :requirement.kind/attachment)))
                                                   (map :requirement/attachment-type))]
                (every? attachment-type-present? required-attachment-types)))})

(def integration-external-id
  {:name    :process.guard/application-has-external-id?
   :type    :guard
   :handler (fn [fsm]
              (let [application (-> fsm :request :target)
                    workspace   (:entity/parent application)
                    group       (:domain/authority-group workspace)
                    group-id    (:group/id group)]
                (if (guard-utils/integration-available? fsm)
                  (or (-> application app-model/external-id str/blank? not)
                      (throw (ex-info "No integration external id"
                                      {:error-translation-keys [[(keyword "backend-system" (name group-id))
                                                                 :backend-system/generic]]})))
                  true)))})

(def workspace-not-sent-to-integration?
  {:name    :process.guard/workspace-not-sent-to-integration?
   :type    :guard
   :handler (fn [fsm]
              (let [{:workspace/keys [external-id legacy-permit?]} (find-workspace fsm)]
                (or (not external-id)
                    legacy-permit?)))})

(def single-verdict-allowed
  {:name    :process.guard/single-verdict-allowed
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   :request
                   :target
                   verdict-model/application-verdicts
                   empty?))})

(def mandatory-permit-fields-set
  {:name    :process.guard/mandatory-permit-fields-set
   :type    :guard
   :handler (fn [fsm]
              (->> (guard-utils/find-child-entity fsm :id)
                   eu/descendants
                   (filter :verdict.section/required?)
                   (every? verdict-section/has-valid-value?)))})

(def parent-permit-in-state
  {:name    :process.guard/parent-permit-in-state
   :type    :guard
   :handler (fn [{[_ states] ::tk/guard
                  :as        fsm}]
              (let [verdict (or (->> (guard-utils/find-child-entity fsm :section-id)
                                     eu/with-ancestors
                                     (filter (eu/is-type-fn :entity.type/verdict))
                                     first)
                                ;; if section id not specified, assume first verdict
                                (->> fsm :request :target
                                     (du/find-descendant {:entity/type :entity.type/verdict})))]
                (->> verdict :domain/state (contains? states))))})

(def acceptance-can-be-responded-to-guard
  {:name    :process.guard/acceptance-can-be-responded-to?
   :type    :guard
   :summary "Check if acceptance is in a state where a reply from applicant is expected"
   :handler (fn [fsm]
              (-> fsm
                  (guard-utils/find-child-entity :acceptance-id)
                  acceptance-model/acceptance-can-be-responded-to?))})

(def changes-requested-for-form?
  {:name    :process.guard/changes-requested-for-form?
   :type    :guard
   :summary "Checks that form either has an acceptance associated in a state that means changes
             are expected of the applicant OR that the form has been added after the application
             was submitted and the acceptance process is not yet started."
   :handler (fn [fsm]
              (let [form         (guard-utils/find-child-entity fsm :form-id)
                    submit-date  (-> fsm :request :target :application/submitted)
                    form-created (:entity/created form)
                    acceptance   (->> form
                                      acceptance-model/acceptances-for-entity
                                      du/latest-entity)]
                (or
                  ;; no acceptance and form is added after submission of the application
                  (and (nil? acceptance)
                       form-created
                       submit-date
                       (calendar/before submit-date form-created))
                  ;; has acceptance that is in the correct state
                  (acceptance-model/acceptance-can-be-responded-to? acceptance))))})

(def required-fields-for-acceptance-target-filled?
  {:name    :process.guard/required-fields-for-acceptance-target-filled?
   :type    :guard
   :summary "Ensure required form fields are filled before marking form change requests done."
   :handler (fn [{[_ {:keys [form-id acceptance-id]}] ::tk/signal
                  :as                                 fsm}]
              (let [form (cond
                           form-id
                           (guard-utils/find-child-entity fsm :form-id)

                           acceptance-id
                           (some-> (guard-utils/find-child-entity fsm :acceptance-id)
                                   :entity/parent
                                   (#(when (eu/is-type? :entity.type/form %) %))))]
                (or (nil? form)
                    (form-model/form-required-fields-set? form))))})

(def form-not-locked-when-signed-off?
  {:name    :process.guard/form-not-locked-when-signed-off?
   :type    :guard
   :handler (fn [fsm]
              (p/if-all-let [application (-> fsm :request :target)
                             form (guard-utils/find-child-entity fsm :form-id)]
                (not (sign-off.model/disabled-when-application-signed-off? application form))))})

(defn- affidavit-missing? [fsm]
  (p/if-all-let [form (-> fsm :request :target :application/sign-off-form)
                 affidavit-field (form-model/get-form-field form :affidavit)]
    (->> affidavit-field
         form-model/file-input-has-file?
         not)
    false))

(def child-entity-required-permissions
  "The guard expects two arguments, the first being the key under which the child entity id is in the signal payload
   and the other a vector of required permissions for this guard to succeed. e.g.:
   `[:process.guard/child-entity-required-permissions :approval-id [:permission.approval/write]]`"
  {:name    :process.guard/child-entity-required-permissions
   :type    :guard
   :handler (fn [fsm]
              (p/if-all-let [[_ entity-k required-permissions] (-> fsm :tilakone.core/guard)
                             target-entity (guard-utils/find-child-entity fsm entity-k)
                             granted-perms (set (permission-resolver/permissions target-entity (-> fsm :core.user/user)))]
                (every? granted-perms required-permissions)))})

(def child-entity-required-write-permissions-granted?
  {:name    :process.guard/child-entity-required-write-permissions-granted?
   :type    :guard
   :handler (fn [fsm]
              (p/if-all-let [[_ entity-k] (-> fsm :tilakone.core/guard)
                             target-entity (guard-utils/find-child-entity fsm entity-k)
                             required-permissions (-> target-entity :entity/required-write-permissions set)
                             granted-perms (set (permission-resolver/permissions target-entity (-> fsm :core.user/user)))]
                (every? granted-perms required-permissions)))})

(def approval-assignee-or-write-authority?
  {:name    :process.guard/approval-assignee-or-write-authority?
   :type    :guard
   :handler (fn [fsm]
              (p/if-all-let [target-entity (guard-utils/find-child-entity fsm :approval-id)
                             user (:core.user/user fsm)]
                (let [task     (-> target-entity :task/_reference first)
                      group-id (:db/id (:task/user-group task))]
                  (or (->> task
                           :task/assignees
                           (some #(= (:db/id %) (:db/id user))))
                      ;; When task is assigned to the group (no specific user), allow any group member
                      (when (and group-id
                                 (->> task :task/assignees (some #(= (:db/id %) group-id))))
                        #?(:clj  (some #(= (:db/id %) group-id) (:user/groups user))
                           :cljs (contains? (:user/groups user) group-id)))
                      (-> (group-model/group-roles-for-user (-> fsm find-workspace :domain/authority-group) user)
                          (contains? :group.role/write-auth))))))})

(def is-sign-off-applicable?
  {:name    :process.guard/is-sign-off-applicable?
   :type    :guard
   :summary (str "Is the application config-tree such that a sign-off form needs to be filled? Sign-off can be done "
                 "or not done, this is just checking if it's applicable to do it at some point.")
   :handler (fn [{{:keys [target]} :request}]
              (-> target :application/sign-off-form some?))})

(defn- sign-off-status
  [fsm]
  (sign-off.model/sign-off-status
    (some-> fsm :request :target :application/sign-off-form)
    (-> fsm party-guards/sign-off-required-role-keys set)))

(def sign-off-required?
  {:name    :process.guard/sign-off-required?
   :type    :guard
   :summary "Is the sign-off both applicable and not yet fully done?"
   :handler (p/fn-> sign-off-status ::sign-off.model/required)})


(defn- signed-off-by-required-roles
  "Checks if all required roles have signed off the application"
  [fsm]
  (let [target             (-> fsm :request :target)
        required-roles     (party-guards/sign-off-required-role-keys fsm)
        signed-roles       (sign-off-status fsm)
        has-sign-off-form? (:application/sign-off-form target)]
    (if has-sign-off-form?
      (every? #(contains? signed-roles %) required-roles)
      true)))


;; Checks that every required role for the given application has done a sign-off
;; or that sign off is not required (no required roles)
(def sign-off?
  {:name    :process.guard/sign-off?
   :type    :guard
   :summary "Is the sign-off fully done?"
   :handler signed-off-by-required-roles})


;; True, if sign off is not done by everyone of the given list of roles
;; (although it might be done for some of them).
(def sign-off-missing-from-required-roles
  {:name    :process.guard/sign-off-missing-from-required-roles
   :type    :guard
   :summary "Is the sign-off *not* fully done?"
   :handler (complement signed-off-by-required-roles)})

(def sign-off-or-affidavit-missing
  {:name    :process.guard/sign-off-or-affidavit-missing
   :type    :guard
   :summary "Is the sign-off either not done *or* the affidavit, if applicable, not uploaded?"
   :handler (fn [fsm]
              (or (not (signed-off-by-required-roles fsm))
                  (affidavit-missing? fsm)))})


(def sign-off-not-done?
  {:name    :process.guard/sign-off-not-done?
   :type    :guard
   :summary (str "Is the sign-off either not yet done *or* not applicable at all. In other words, is there an "
                 "existing sign-off that prevents some change from being done?")
   ;; True if the sign-off is not required or not signed.
   :handler #(-> % sign-off-status ::sign-off.model/signed not)})

(def sign-off-done-or-not-required-after-submission-changes?
  {:name    :process.guard/sign-off-done-or-not-required-after-submission-changes?
   :type    :guard
   :summary (str "Passes if the global feature flag and config flag for requiring sign-off after requested changes "
                 "are not both enabled, or if sign-off is done.")
   :handler (fn [fsm]
              (let [workspace       (find-workspace fsm)
                    config-enabled? (config-model/resolve-require-sign-off-after-changes-requested?
                                      (-> fsm :request :feature-flags)
                                      workspace)]
                (or (not config-enabled?)
                    (signed-off-by-required-roles fsm))))})

(def sign-off-required-after-submission-changes?
  {:name    :process.guard/sign-off-required-after-submission-changes?
   :type    :guard
   :summary "Passes if submitted-review changes require a fresh application sign-off before resubmission."
   :handler (fn [fsm]
              (config-model/resolve-require-sign-off-after-changes-requested?
                (-> fsm :request :feature-flags)
                (find-workspace fsm)))})

(def sign-off-not-required-after-submission-changes?
  {:name    :process.guard/sign-off-not-required-after-submission-changes?
   :type    :guard
   :summary "Passes if submitted-review changes do not require a fresh application sign-off before resubmission."
   :handler (complement (:handler sign-off-required-after-submission-changes?))})


(defn- signed-off-by-authority [fsm]
  (some-> fsm
          :request
          :target
          :application/sign-off-form
          sign-off.model/signed-by-authority))

(defn- signed-off [fsm]
  (-> fsm
      :request
      :target
      :application/sign-off-form
      sign-off.model/form-all-sign-offs
      seq
      boolean))


;; True, if at least one sign off exists
(def sign-off-started?
  {:name    :process.guard/sign-off-started?
   :type    :guard
   :summary (str "Has *some* party already signed of, regardless of whether the whole sign-off is completed by "
                 "all required parties")
   :handler signed-off})


;; True, if all required sign-offs are made by an authority
(def sign-off-done-by-authority
  {:name    :process.guard/sign-off-done-by-authority
   :type    :guard
   :summary "Is the sign-off done and done by an authority user?"
   :handler (every-pred signed-off
                        signed-off-by-authority)})


(def sign-off-done-by-non-authority
  {:name    :process.guard/sign-off-done-by-non-authority
   :type    :guard
   :summary "Is the sign-off done and *not* by an authority, i.e. has an actual applicant done the sign-off"
   :handler (every-pred signed-off
                        (complement signed-off-by-authority))})


(defn sign-off-upload-only-handler [fsm]
  (let [fsm-target (-> fsm :request :target)
        role-id    (guard-utils/get-param fsm :role-id)]
    (boolean
      (or (some-> fsm-target
                  :application/sign-off-form
                  (form-model/find-elements [:sign-off/signatures {:upload-only? true}])
                  not-empty)
          (party-model/role-only-has-non-user-parties? fsm-target
                                                       (-> fsm-target
                                                           group-model/get-authority-group
                                                           :party-role/_authority-group
                                                           (p/find-first {:db/id role-id})
                                                           :party-role/key))))))


(def sign-off-upload-only?
  {:name    :process.guard/sign-off-upload-only?
   :type    :guard
   :summary (str "Is the application missing the required parties for sign-of, in other words, will the sign-off "
                 "need to be done by uploading an authorization letter?")
   :handler sign-off-upload-only-handler})


(def sign-off-not-upload-only?
  {:name    :process.guard/sign-off-not-upload-only?
   :type    :guard
   :summary (str "Are the sign-off role requirements met by parties with an actual user linked to them, ie. can the "
                 "sign-off be done digitally by end user without uploading an authorization letter?")
   :handler (complement sign-off-upload-only-handler)})

(defn group-has-feature? [fsm]
  (guard-utils/group-feature-available? fsm (-> fsm ::tk/guard second)))

(defn domain-group-has-feature? [fsm]
  (guard-utils/domain-group-feature-available? fsm (-> fsm ::tk/guard second)))

(def group-feature?
  {:name    :process.guard/group-feature?
   :type    :guard
   ;; True if the given feature is enabled in the application authority group.
   :handler group-has-feature?})

(def group-feature-disabled?
  {:name    :process.guard/group-feature-disabled?
   :type    :guard
   ;; True if the given feature is not enabled in the application authority group.
   :handler (complement group-has-feature?)})

(def domain-group-feature?
  {:name    :process.guard/domain-group-feature?
   :type    :guard
   ;; True if the given feature is enabled in the domain group.
   :handler domain-group-has-feature?})

(def workspace-child-in-state
  {:name    :process.guard/workspace-child-in-state
   :type    :guard
   :handler (fn [{::tk/keys [guard] :as fsm}]
              (let [[_ id-param allowed-state-set] guard
                    workspace                      (find-workspace fsm)
                    target-entity                  (du/find-descendant {:db/id (guard-utils/get-param fsm id-param)}
                                                                       workspace)
                    allowed-state                  (set allowed-state-set)]
                (allowed-state (:domain/state target-entity))))})

(def not-linked-to-order?
  {:name    :process.guard/not-linked-to-order?
   :type    :guard
   :handler (fn [{::tk/keys [guard] :as fsm}]
              (let [[_ id-param] guard
                    execution    (-> fsm :request :target)
                    deficiency   (du/find-descendant {:db/id (guard-utils/get-param fsm id-param)}
                                                     execution)
                    orders       (inspection-model/deficiency-linked-orders deficiency)]
                (empty? orders)))})

(def lock-attrs-after-permit-issued
  "Prevent editing an attribute for an entity that is linked to a verdict
  section, if the verdict has been (or is being) issued."
  {:name    :process.guard/lock-attrs-after-permit-issued
   :type    :guard
   :handler (fn [{::tk/keys [guard signal] :as fsm}]
              (let [[_ locked-attrs]      guard
                    [_ {:keys [attr id]}] signal]
                (if (locked-attrs attr)
                  (let [workspace            (find-workspace fsm)
                        {state :domain/state
                         :as   verdict}      (du/find-descendant #(-> %
                                                                      :entity/type
                                                                      (= :entity.type/verdict))
                                                                 workspace)
                        has-entity?          ((->> verdict
                                                   eu/descendants
                                                   (mapcat :verdict.section/entities)
                                                   (map :db/id)
                                                   set) id)]
                    (or (= :verdict.state/draft state)
                        (not has-entity?)))
                  true)))})


(defn get-inspection [{[_ params]  ::tk/signal
                       [_ id-attr] ::tk/guard
                       :as         fsm}]
  (let [workspace     (find-workspace fsm)
        inspection-id (get params id-attr)]
    (du/find-descendant {:db/id inspection-id}
                        workspace)))


(defn children [pred e]
  (->> e
       :entity/_parent
       (filter pred)))


(def get-visits
  (partial children inspection-model/is-visit?))

(def get-bookings
  (partial children (partial eu/is-type? :entity.type/booking)))


(defn inspection-visits-in-progress [fsm inspection-id]
  (let [workspace  (find-workspace fsm)
        inspection (du/find-descendant {:db/id inspection-id}
                                       workspace)]
    (->> inspection
         get-visits
         (filter (du/in-state-fn :inspection.visit.state/in-progress)))))

(defn no-visits-ongoing-handler [{[_ {:keys [inspection-id]}] ::tk/signal :as fsm}]
  (-> (inspection-visits-in-progress fsm inspection-id)
      empty?))

(def no-visits-ongoing
  {:name    :process.guard/no-visits-ongoing
   :type    :guard
   :handler no-visits-ongoing-handler})

(def visit-ongoing
  {:name    :process.guard/visit-ongoing
   :type    :guard
   :handler (complement no-visits-ongoing-handler)})


(def inspection-visit-ongoing
  {:name    :process.guard/inspection-visit-ongoing
   :type    :guard
   :handler (fn [{[_ {:keys [inspection-id]}] ::tk/signal :as fsm}]
              (-> (inspection-visits-in-progress fsm inspection-id)
                  seq))})


(def inspection-visits-in-states
  {:name    :process.guard/inspection-visits-in-states
   :type    :guard
   :summary "Check if the workspace has at least one inspection visit in any of the given states"
   :handler (fn [{[_ states] ::tk/guard
                  :as        fsm}]
              (->> fsm
                   find-workspace
                   workspace-model/workspace-execution
                   inspection-model/phase-inspections
                   (mapcat get-visits)
                   (some (apply du/in-state-fn states))))})


(def require-permission-for-attr
  ;; given a signal, require that the user has the specified permission on the target
  ;; if any of the specified attributes are included in the signal parameters
  {:name    :process.guard/require-permission-for-attr
   :type    :guard
   :handler (fn [{::tk/keys        [guard signal]
                  {target :target} :request
                  user             :core.user/user}]
              (let [[_ role attrs] guard
                    [_ params]     signal]
                (if (-> params
                        keys
                        set
                        (set/intersection attrs)
                        empty?)
                  true
                  (-> target
                      (permission-resolver/permissions user)
                      set
                      (contains? role)))))})


(def no-visits
  {:name    :process.guard/no-visits
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   get-inspection
                   get-visits
                   (filter du/is-active?)
                   empty?))})


(def no-valid-bookings
  {:name    :process.guard/no-valid-bookings
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   get-inspection
                   get-bookings
                   (some (comp not
                               #{:booking.state/cancelled
                                 :booking.state/fulfilled}
                               :domain/state))
                   not))})


(def lock-attrs-after-visit-started
  {:name    :process.guard/lock-attrs-after-visit-started
   :type    :guard
   :handler (fn [{::tk/keys [guard signal] :as fsm}]
              (let [[_ _ locked-attrs] guard
                    [_ {:keys [attr]}] signal]
                (or (not (contains? locked-attrs attr))
                    ((:handler no-visits) fsm))))})


(def allow-attrs
  "For a normal entity edit request (i.e. having keys :attr :value etc), make
  sure that the attribute being edited is one in a set of allowed values."
  {:name    :process.guard/allow-attrs
   :type    :guard
   :handler (fn [{::tk/keys [guard signal]}]
              (let [[_ allowed-attrs]  guard
                    [_ {:keys [attr]}] signal]
                (contains? allowed-attrs attr)))})


(def required-fields-for-form-filled?
  "Ensure required form fields, other than the one operated on by the signal,
   are filled."
  {:name    :process.guard/required-fields-for-form-filled?
   :type    :guard
   :handler (fn [{{:keys [target]}                :request
                  [_ {:keys [form-id field-key]}] ::tk/signal}]
              (let [form (->> target
                              (du/find-descendant {:db/id form-id}))]
                (form-model/form-required-fields-set? form #{field-key})))})


(def form-confirmation-needed?
  "True when the form operated on by the signal needs confirmation from the current user."
  {:name    :process.guard/form-confirmation-needed?
   :type    :guard
   :handler (fn [{{:keys [target]}      :request
                  user                  :core.user/user
                  [_ {:keys [form-id]}] ::tk/signal}]
              (let [form (->> target
                              (du/find-descendant {:db/id form-id}))]
                (not (form-model/form-confirmation-satisfied? form user))))})


(defn- find-field-config-by-field-key [target form-id field-key]
  (when field-key
    (-> (du/find-descendant {:db/id form-id} target)
        (form-model/field-config-by-field-key field-key))))


(def signature-role-requirement-met?
  "Checks that the `:required-role` of a `:signature` form field is met"
  {:name    :process.guard/signature-role-requirement-met?
   :type    :guard
   :handler (fn [{{:keys [target]}                :request
                  [_ {:keys [form-id field-key]}] ::tk/signal
                  {user-id :db/id}                :core.user/user}]
              (if-let [required-role (some->> (find-field-config-by-field-key target form-id field-key)
                                              :required-role
                                              (p/wrap-into [])
                                              seq)]
                (let [my-roles (->> target
                                    :entity/_parent
                                    (filter party-model/active?)
                                    (filter (comp (partial = user-id)
                                                  :db/id
                                                  :party/user))
                                    first
                                    party-model/role-keys)]
                  (some my-roles required-role))
                true))})


(def permit-signature-role-requirement-met?
  "Checks that the user has a sufficient role to sign the permit"
  {:name    :process.guard/permit-signature-role-requirement-met?
   :type    :guard
   :handler (fn [{{:keys [target]} :request
                  {user-id :db/id} :core.user/user}]
              (let [my-roles (->> target
                                  :entity/_parent
                                  (filter party-model/active?)
                                  (filter (comp (partial = user-id)
                                                :db/id
                                                :party/user))
                                  first
                                  party-model/role-keys)]
                (some my-roles verdict-model/permit-sign-off-roles)))})

(def sign-off-by-form-party?
  "Checks that the current user is the party on the form being signed, or
   there is no party or the party has no user associated"
  {:name    :process.guard/sign-off-by-form-party?
   :type    :guard
   :handler (fn [{{:keys [target]}      :request
                  {user-id :db/id}      :core.user/user
                  [_ {:keys [form-id]}] ::tk/signal}]
              (let [form-user-id (some->> target
                                          (du/find-descendant {:db/id form-id})
                                          :form/party
                                          :party/user
                                          :db/id)]
                (or (nil? form-user-id)
                    (= user-id form-user-id))))})

(def group-role-requirement-met?
  "Checks that the `:required-role` of a `:signature` form field is met"
  {:name    :process.guard/group-role-requirement-met?
   :type    :guard
   :handler (fn [{{:keys [target]}                :request
                  [_ {:keys [form-id field-key]}] ::tk/signal
                  user                            :core.user/user}]
              (if-let [required-role (-> (find-field-config-by-field-key target form-id field-key)
                                         :required-group)]
                (-> target
                    :entity/parent
                    :domain/authority-group
                    (group-model/group-roles-for-user user)
                    (contains? required-role))
                true))})


(def sign-off-role-requirement-met?
  "Checks that the `party-id` is a party in the application, controlled by the current user,
   and it has a party-role `role-id` that signature is required for, and the role has not yet signed."
  {:name    :process.guard/sign-off-role-requirement-met?
   :type    :guard
   :handler (fn [{{:keys [target]}               :request
                  [_ {:keys [party-id role-id]}] ::tk/signal
                  user                           :core.user/user
                  :as                            fsm}]
              (let [party           (-> (party-model/phase-parties target)
                                        (p/find-first (fn [party]
                                                        (and (= (:db/id party) party-id)
                                                             (party-model/user-can-act-as-party? user party)))))
                    user-party-role (some-> party
                                            :party/party-roles
                                            (p/find-first {:db/id role-id}))
                    roles-in-config (-> fsm
                                        party-guards/sign-off-required-role-keys
                                        set)
                    already-signed? (->> target
                                         :application/sign-off-form
                                         sign-off.model/form-all-sign-offs
                                         (some #(-> % :sign-off/party-role :db/id (= role-id))))]
                (and user-party-role
                     (roles-in-config (:party-role/key user-party-role))
                     (not already-signed?))))})


(def form-signed-state-is
  "If arg :signed-state-should-be = :inactive, handler-fn returns true if form has no active signatures.
   If arg :signed-state-should-be != :inactive, handler-fn returns true if form has 1 or more active signatures

  For `:active`, always returns true if form-field-id is not specified (to allow form-level check).

  The lock does not apply to authority-only (:required-role) form fields."
  {:name    :process.guard/form-signed-state-is
   :type    :guard
   :handler (fn [{{:keys [target]}                :request
                  [_ {:keys [form-id field-key]}] ::tk/signal
                  [_ signed-state-should-be]      ::tk/guard}]
              (or
                ;; if checking for active signature, skip if missing form-field-id so that
                ;; top-level form-wide check passes
                (and (nil? field-key)
                     (= signed-state-should-be :inactive))
                ;; form-field is restricted to a specific group -> do not lock as it
                ;; it an authority only field
                (-> (find-field-config-by-field-key target form-id field-key)
                    :required-group
                    some?)
                ;; otherwise, lock if there are any signatures
                (cond-> (form-model/form-has-active-signature? (du/find-descendant {:db/id form-id} target))
                  (= signed-state-should-be :inactive) not)))})


(defn- bills-in-state-handler [{{:keys [target]} :request
                                [_ & states]     ::tk/guard}]
  (->> target
       fee-model/phase-bills
       (some (apply du/in-state-fn states))
       boolean))


(def no-bills-in-state?
  {:name    :process.guard/no-bills-in-state?
   :type    :guard
   :handler (complement bills-in-state-handler)})


(def some-bills-in-state?
  {:name    :process.guard/some-bills-in-state?
   :type    :guard
   :handler bills-in-state-handler})


(def bill-phase-one-of
  {:name    :process.guard/bill-phase-one-of
   :type    :guard
   :handler (fn [{[_ {:keys [phase]}] ::tk/signal
                  [_ allowed-phases]  ::tk/guard}]
              (contains? (set allowed-phases) phase))})


(def other-bills-paid?
  {:name    :process.guard/other-bills-paid?
   :type    :guard
   :handler (fn [fsm]
              (let [application   (-> fsm :request :target)
                    target-entity (guard-utils/find-child-entity fsm :bill-id)]
                (->> application
                     :entity/_parent
                     (filter #(and (eu/is-type? :entity.type/bill %)
                                   ((du/in-state-fn :bill.state/unpaid) %)
                                   (not= (:db/id %) (:db/id target-entity))))
                     empty?)))})


(def bills-paid-before-inspection-request
  {:name    :process.guard/bills-paid-before-inspection-request
   :type    :guard
   :handler (fn [{{:keys [target feature-flags]} :request
                  [_ {:keys [inspection-id]}]    ::tk/signal
                  :as                            fsm}]
              (let [inspection      (du/entity-for-db-of target inspection-id)
                    authority?      (guard-utils/has-permission? fsm :permission.application/issue-permit)
                    feature-active? (and (-> inspection
                                             :inspection/template
                                             :entity/parent
                                             :inspection.template.kind/prevent-request-when-unpaid?)
                                         (contains? feature-flags :feature-flag.id/prevent-request-when-unpaid))
                    bills-paid?     (->> target
                                         fee-model/phase-bills
                                         (every? (du/in-state-fn :bill.state/paid :bill.state/canceled :bill.state/draft)))]
                (or
                  bills-paid?
                  authority?
                  (not feature-active?))))})

(def bill-has-external-id?
  "Check that bill has external id, i.e. has been synced to back-end.
   If the group has no fee integration, always returns `true`"
  {:name    :process.guard/bill-has-external-id?
   :type    :guard
   :handler (fn [fsm]
              (if (guard-utils/fee-integration-available? fsm)
                (-> (guard-utils/find-child-entity fsm :bill-id)
                    :invoice/external-id
                    some?)
                true))})


(def some-bill-integration-sendable?
  "Check that at least one confirmed or paid bill has not yet been synced to back-end system."
  {:name    :process.guard/some-bill-integration-sendable?
   :type    :guard
   :handler (fn [{{{workspace :entity/parent} :target} :request}]
              (-> workspace fee-model/back-end-sendable-workspace-bills some?))})


(def bill-has-payer?
  "Check that a payer has been selected for the bill.
   If the group has no PDF bill generation enabled, always returns `true`"
  {:name    :process.guard/bill-has-payer?
   :type    :guard
   :handler (fn [{[_ {:keys [bill-id]}] ::tk/signal
                  :as                   fsm}]
              (if (guard-utils/generate-bill-pdf-available? fsm)
                (or
                  (nil? bill-id)
                  (-> (guard-utils/find-child-entity fsm :bill-id)
                      :bill/payer
                      some?))
                true))})


(def bill-has-no-child?
  "Check if a bill has no existing child (reversal) bill.
   Used to prevent creating multiple reversal drafts for the same bill."
  {:name    :process.guard/bill-has-no-child?
   :type    :guard
   :handler (fn [fsm]
              (let [bill (guard-utils/find-child-entity fsm :bill-id)]
                (nil? (:bill/next-bill bill))))})


(defn- ws-has-flag? [{{:keys [target]}  :request
                      [_ required-flag] ::tk/guard}]
  (-> target
      :entity/parent
      required-flag))


(def workspace-has-flag?
  {:name    :process.guard/workspace-has-flag?
   :type    :guard
   :handler ws-has-flag?})


(def workspace-does-not-have-flag?
  {:name    :process.guard/workspace-does-not-have-flag?
   :type    :guard
   :handler (complement ws-has-flag?)})

(defn target-has-flag-handler [{{:keys [target]}  :request
                                [_ required-flag] ::tk/guard}]
  (-> target required-flag))

(def target-has-flag?
  {:name    :process.guard/target-has-flag?
   :type    :guard
   :handler target-has-flag-handler})

(def target-does-not-have-flag?
  {:name    :process.guard/target-does-not-have-flag?
   :type    :guard
   :handler (complement target-has-flag-handler)})


(def target-or-sibling-has-flag?
  {:name    :process.guard/target-or-sibling-has-flag?
   :type    :guard
   :summary "Checks if target has given flag set or target's sibling of given type has another flag set."
   :handler (fn [{{:keys [target]}                                       :request
                  [_ required-flag [sibling-type required-sibling-flag]] ::tk/guard}]
              (or (-> target required-flag)
                  (-> target
                      :entity/parent
                      ((child/child-entity-of-type-fn sibling-type))
                      required-sibling-flag)))})


(defn- workspace-children [workspace child-types]
  (->> child-types
       (mapcat #((child/child-entities-of-type-fn %) workspace))
       seq))


(defn workspace-has-children-handler [{{{workspace :entity/parent} :target} :request
                                       [_ child-types]                      ::tk/guard}]
  (workspace-children workspace child-types))


(def workspace-does-not-have-children?
  {:name    :process.guard/workspace-does-not-have-children?
   :type    :guard
   :handler (complement workspace-has-children-handler)})


(def workspace-has-children?
  {:name    :process.guard/workspace-has-children?
   :type    :guard
   :handler workspace-has-children-handler})


(def partial-occupancy-enabled?
  {:name    :process.guard/partial-occupancy-enabled?
   :type    :guard
   :handler (fn [fsm]
              (-> fsm
                  :request
                  :target
                  (execution-model/feature? :execution.feature/partial-occupation)))})


(defn- partially-submitted-handler [{{:keys [target]} :request}]
  (-> target :application/partial-submit-by some?))


(def partially-submitted?
  {:name    :process.guard/partially-submitted?
   :type    :guard
   :handler partially-submitted-handler})


(def not-partially-submitted?
  {:name    :process.guard/not-partially-submitted?
   :type    :guard
   :handler (complement partially-submitted-handler)})


(def never-submitted?
  "Check that the application has never been submitted, even if it's been reverted to a draft"
  {:name    :process.guard/never-submitted?
   :type    :guard
   :handler (fn [fsm]
              (let [application (-> fsm find-workspace workspace-model/workspace-application)
                    sof         (:application/sign-off-form application)]
                (and (nil? (:application/original-submitted application))
                     (or (nil? (:application/submitted application))
                         (true? (:application/submitted-set-manually? application)))
                     (empty? (acceptance-model/acceptances-for-entity sof)))))})


(defn- pre-consultation-partially-submitted-handler [{{:keys [target]} :request}]
  (-> target :pre-consultation/partial-submit-by some?))


(def pre-consultation-partially-submitted?
  {:name    :process.guard/pre-consultation-partially-submitted?
   :type    :guard
   :handler pre-consultation-partially-submitted-handler})


(def pre-consultation-not-partially-submitted?
  {:name    :process.guard/pre-consultation-not-partially-submitted?
   :type    :guard
   :handler (complement pre-consultation-partially-submitted-handler)})


(defn- ignore-for-only-workspace-child? [child]
  (or ((eu/is-type-fn :entity.type/execution
                      :entity.type/conversation-thread
                      :entity.type/audit-event
                      :entity.type/email-log
                      :entity.type/package-pdf)
       child)
      (and (eu/is-type? :entity.type/file child)
           (= :file.category/attachment-candidate (:file/category child)))))


(def multi-phase-workspace?
  {:name    :process.guard/multi-phase-workspace?
   :type    :guard
   :handler (fn [fsm]
              (let [phases (workspace-model/phase-entities (find-workspace fsm))]
                (some? (next phases))))})


(def only-workspace-child?
  {:name    :process.guard/only-workspace-child?
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   find-workspace
                   :entity/_parent
                   ;; TODO now execution is created early, execution-only
                   ;;      workspaces are not deleted. To be changed when
                   ;;      execution creation is deferred.
                   (remove ignore-for-only-workspace-child?)
                   count
                   (= 1)))})


(def work-not-locked?
  "Checks if setting the work type and target has been disallowed on the application itself"
  {:name    :process.guard/work-not-locked?
   :type    :guard
   :handler (fn [fsm]
              (-> fsm :request :target :application/work-locked? not))})


(def work-set-not-prevented?
  "Checks if setting the work type and target has been disallowed in the category configuration"
  {:name    :process.guard/work-set-not-prevented?
   :type    :guard
   :handler (fn [fsm]
              (-> fsm :request :target app-model/work-set-prevented? not))})

(def not-last-work-type
  "For multiple work-type application, ensure that the given work-type to remove is not the last"
  {:name    :process.guard/not-last-work-type
   :type    :guard
   :handler (fn [fsm]
              (-> fsm :request :target :application/work-type-group-configs count (> 1)))})

(defn config-flag-enabled-handler [{:keys          [request]
                                    [_ config-key] ::tk/guard}]
  (->> request
       :target
       :entity/parent
       (config-model/resolve-boolean config-key)))

(def config-flag-enabled?
  {:name    :process.guard/config-flag-enabled?
   :type    :guard
   :handler config-flag-enabled-handler})

(def config-flag-not-enabled?
  {:name    :process.guard/config-flag-not-enabled?
   :type    :guard
   :handler (complement config-flag-enabled-handler)})

(defn target-inspections [fsm]
  (-> fsm
      :request
      :target
      inspection-model/phase-inspections))

(def all-inspections-passed?
  {:name    :process.guard/all-inspections-passed?
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   target-inspections
                   (every? (du/in-state-fn :inspection.state/completed
                                           :inspection.state/cancelled))))})

(def all-inspections-passed-except?
  {:name    :process.guard/all-inspections-passed-except?
   :type    :guard
   :summary "Excludes target inspection from checked inspections."
   :handler (fn [fsm]
              (let [excluded (-> fsm (guard-utils/find-child-entity :visit-id) :entity/parent :db/id)]
                (->> fsm
                     target-inspections
                     (remove #(= excluded (:db/id %)))
                     (every? (du/in-state-fn :inspection.state/completed
                                             :inspection.state/cancelled)))))})

(def no-non-final-inspection-open-when-final-inspection?
  {:name    :process.guard/no-non-final-inspection-open-when-final-inspection?
   :type    :guard
   :summary "If target is final inspection, return false if there are any non-final inspections not completed,
   or cancelled. Otherwise return true."
   :handler (fn [fsm]
              (let [target-inspection            (-> fsm (guard-utils/find-child-entity :visit-id) :entity/parent)
                    target-inspection-id         (:db/id target-inspection)
                    target-inspection-non-final? (-> target-inspection inspection-model/final-inspection? not)]
                (or target-inspection-non-final?
                    (->> fsm
                         target-inspections
                         (remove #(= target-inspection-id (:db/id %)))
                         (remove inspection-model/final-inspection?)
                         (every? (du/in-state-fn :inspection.state/completed
                                                 :inspection.state/cancelled))))))})

(def has-no-inspections?
  {:name    :process.guard/has-no-inspections?
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   :request
                   :target
                   inspection-model/phase-inspections
                   (remove (du/in-state-fn :inspection.state/cancelled))
                   count
                   (= 0)))})

(def has-inspection?
  {:name    :process.guard/has-inspections?
   :type    :guard
   :handler (-> has-no-inspections? :handler complement)})

(def all-orders-cleared?
  {:name    :process.guard/all-orders-cleared?
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   :request
                   :target
                   inspection-model/all-phase-orders-resolved?))})


(defn- can-create-report-handler [entity-type]
  (fn [fsm]
    (->> fsm
         :request
         :target
         :entity/_parent
         ;; Check that there are no unpublished reports of the requested type, as there can only be one at a time
         (filter #(and (eu/is-type? entity-type %)
                       (nil? (:planning-report/published %))))
         empty?)))


(def can-create-draft-report?
  {:name    :process.guard/can-create-draft-report?
   :type    :guard
   :handler (can-create-report-handler :entity.type/planning-draft-report)})


(def can-create-final-report?
  {:name    :process.guard/can-create-final-report?
   :type    :guard
   :handler (can-create-report-handler :entity.type/planning-final-report)})



(def application-has-completed-approval-comments?
  {:name    :process.guard/application-has-completed-approval-comments?
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   :request
                   :target
                   planning-report-model/completed-comments?))})


(def application-has-council-decision?
  {:name    :process.guard/application-has-council-decision?
   :type    :guard
   :handler (fn [{{application :target} :request
                  [_ decision-kind]     ::tk/guard}]
              (->> (eu/descendants :entity.type/council-decision application)
                   (some #(-> % :council-decision/kind (= decision-kind)))))})


(def report-has-unsynced-approval-comments?
  {:name    :process.guard/report-has-unsynced-approval-comments?
   :type    :guard
   :handler (fn [{{:keys [target]} :request
                  [_ {:keys [id]}] ::tk/signal}]
              (planning-report-model/new-comments? target id))})

(def only-form-from-schema?
  {:name    :process.guard/only-form-from-schema?
   :type    :guard
   :handler (fn [{[_ {:keys [form-schema-id]}] ::tk/signal
                  {:keys [target]}             :request}]
              (let [existing-schema-ids (->> target
                                             :entity/_parent
                                             (keep (comp :db/id :form/schema))
                                             set)]
                (-> form-schema-id existing-schema-ids not)))})

(def archiving-enabled?
  {:name    :process.guard/archiving-enabled?
   :type    :guard
   :handler (fn [{{{:keys [:entity/parent]} :target} :request :as fsm}]
              (and (->> parent
                        workspace-model/default-config-path
                        reverse
                        (keep :group.config/archiving-enabled?)
                        first
                        boolean)
                   (guard-utils/group-feature-available? fsm
                                                         :group.feature/archiving)))})

(def archiving-fields-have-value
  {:name    :process.guard/archiving-fields-have-value
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (every? (fn [{:keys [:archiving-field/value :archiving-field/selected-value] :as field}]
                        (when (archiving-model/field-has-non-blank-value? field)
                          (if (archiving-model/manual-input-is-visible? selected-value)
                            ;; if second input is visible it should have value
                            (not= value selected-value)
                            true)))
                      (archiving-model/get-archiving-fields target)))})

(def ws-children-can-be-archived?
  {:name    :process.guard/ws-children-can-be-archived?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (-> target :entity/parent project-model/ws-children-can-be-archived?))})

(def party-in-state?
  {:name    :process.guard/party-in-state?
   :type    :guard
   :handler (fn [{{:keys [target]}       :request
                  [_ & states]           ::tk/guard
                  [_ {:keys [party-id]}] ::tk/signal}]
              (let [in-state? (set states)]
                (->> target
                     (du/find-descendant {:db/id party-id})
                     :domain/state
                     in-state?)))})


(def committee-meeting-item-active?
  {:name    :process.guard/committee-meeting-item-active?
   :type    :guard
   :handler (fn [{[_ {:keys [meeting-item-id]}] ::tk/signal
                  {:keys [target]}              :request}]
              (->> target
                   :entity/parent
                   :committee-item/_item
                   (filter #(-> % :db/id (= meeting-item-id)))
                   (filter (du/in-state-fn :committee-item.state/pending))
                   ;; meeting has to be active as well
                   (some #(->> %
                               :entity/parent
                               (du/in-state? :committee-meeting.state/active)))))})


(def no-pending-pre-con-meetings?
  {:name    :process.guard/no-pending-pre-con-meetings?
   :type    :guard
   :handler (fn [{{:keys [target feature-flags]} :request}]
              (or (not (contains? feature-flags :feature-flag.id/pre-con-meeting-guard))
                  (not (meeting-model/meeting-pending? target))))})

(def locked-attributes-with-user?
  "When the party has a linked user, disallow changing the specified attributes"
  {:name    :process.guard/locked-attributes-with-user?
   :type    :guard
   :handler (fn [{{:keys [target]}         :request
                  [_ & attributes]         ::tk/guard
                  [_ {:keys [party-id
                             field-path]}] ::tk/signal}]
              (or (not (contains? (set attributes) field-path))
                  (->> target
                       (du/find-descendant {:db/id party-id})
                       :party/user
                       nil?)))})

(def not-sign-off-related-form?
  {:name    :process.guard/not-sign-off-related-form?
   :type    :guard
   :summary "Only allow operation on forms that do not include fields used by sign-off. Only check fields
             that aren't also present in sibling forms, though."
   :handler (fn [{{:keys [target]}      :request
                  [_ {:keys [form-id]}] ::tk/signal}]
              (let [sign-off-form (-> target
                                      #?(:clj  sign-off/application-at-first-sign-off-time
                                         :cljs identity)
                                      :application/sign-off-form)]
                (or (nil? sign-off-form)
                    (let [sign-off-fields (->> (sign-off-props/form-fields-referenced-in-props sign-off-form)
                                               set)
                          own-fields      (->> target
                                               (du/find-descendant {:db/id form-id})
                                               form-model/form->field-ids-from-schema)
                          common-fields   (set/intersection own-fields sign-off-fields)]
                      (or (empty? common-fields)
                          (->> target
                               form-model/forms
                               (remove #(= (:db/id %) form-id))
                               (mapcat form-model/form->field-ids-from-schema)
                               set
                               (set/intersection common-fields)
                               empty?
                               not))))))})


(def not-party-form?
  {:name    :process.guard/not-party-form?
   :type    :guard
   :summary "A party form is a form that links to a specific party and role."
   :handler (fn [{{:keys [target]}      :request
                  [_ {:keys [form-id]}] ::tk/signal}]
              (->> target
                   (du/find-descendant {:db/id form-id})
                   :form/party
                   nil?))})


(defn- find-bill-fee [{{:keys [target]}             :request
                       [_ {:keys [bill-id fee-id]}] ::tk/signal}]
  (-> (du/find-descendant {:db/id bill-id} target)
      :bill/fees
      (p/find-first {:db/id fee-id})))


(def fee-bondable?
  {:name    :process.guard/fee-bondable?
   :type    :guard
   :handler (fn [fsm]
              (let [{:keys [:fee/bond-amount :fee/amount :fee-template/bond-payment?]} (find-bill-fee fsm)]
                (and bond-payment?
                     (not bond-amount)
                     (pos? amount))))})


(def fee-bond-cancelable?
  {:name    :process.guard/fee-bond-cancelable?
   :type    :guard
   :handler (fn [{user :core.user/user :as fsm}]
              (let [{:fee/keys [bond-amount external-bond-id bond-party]} (find-bill-fee fsm)]
                (and (number? bond-amount)
                     (pos? bond-amount)
                     external-bond-id
                     (or (authority/authority-in-group? user (-> fsm find-workspace :domain/authority-group))
                         (= (:db/id user) (-> bond-party :party/user :db/id))))))})


(def no-bonded-fees-in-bill?
  {:name    :process.guard/no-bonded-fees-in-bill?
   :type    :guard
   :handler (fn [{{:keys [target]}      :request
                  [_ {:keys [bill-id]}] ::tk/signal}]
              (->> (du/find-descendant {:db/id bill-id} target)
                   :bill/fees
                   (not-any? :fee/bond-amount)))})


(def bill-does-not-contain-refund?
  {:name    :process.guard/bill-does-not-contain-refund?
   :type    :guard
   :handler (fn [{{:keys [target]}      :request
                  [_ {:keys [bill-id]}] ::tk/signal}]
              (->> (du/find-descendant {:db/id bill-id} target)
                   :bill/fees
                   (not-any? :fee/_refunded-by-fee)))})


(def all-conditions-closed?
  {:name    :process.guard/all-conditions-closed?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (->> target
                   :entity/parent
                   application-condition-model/all-conditions-closed?))})


(def no-overdue-conditions?
  {:name    :process.guard/no-overdue-conditions?
   :summary "All permit-specific conditions must not be overdue"
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (->> (application-condition-model/phase-conditions target)
                   (remove application-condition-model/general-condition?)
                   (not-any? application-condition-model/overdue?)))})


(def overdue-conditions?
  {:name    :process.guard/overdue-conditions?
   :summary "At least one permit-specific condition is overdue"
   :type    :guard
   :handler (complement (:handler no-overdue-conditions?))})


(def has-execution-or-all-conditions-closed?
  "Application with open conditions can't be approved if there's no execution phase"
  {:name    :process.guard/has-execution-or-all-conditions-closed?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (let [ws (:entity/parent target)]
                (or (-> ws workspace-model/workspace-execution some?)
                    (-> ws config-model/resolve-omit-execution false?)
                    (application-condition-model/all-conditions-closed? ws))))})


(def condition-attachment-present?
  {:name    :process.guard/condition-attachment-present?
   :type    :guard
   :handler (fn [fsm]
              (let [condition (guard-utils/find-child-entity fsm :id)]
                (or (not (feature-flag-enabled? fsm :feature-flag.id/planning-condition-attachments))
                    (:application.condition/closed condition)
                    (not (application-condition-model/attachment-required-but-missing? condition)))))})


(def condition-not-closed?
  "Condition has not been signed off"
  {:name    :process.guard/condition-not-closed?
   :type    :guard
   :handler (fn [fsm]
              (not (:application.condition/closed
                     (guard-utils/find-child-entity fsm :id))))})


(def form-field-id-is
  "field-key = :form.field/id attr"
  {:name    :process.guard/form-field-id-is
   :type    :guard
   :handler (fn [{[_ {:keys [field-key]}]  ::tk/signal
                  [_ [field-key-to-match]] ::tk/guard}]
              (= field-key field-key-to-match))})


(def not-removing-form-file-created-before-submission?
  "Used to prevent authority from deleting affidavit document that was uploaded by applicant"
  {:name    :process.guard/not-removing-form-file-created-before-submission?
   :type    :guard
   :handler (fn [{{:keys [target]}                                  :request
                  [_ {:keys [form-field-id form-id delete-field?]}] ::tk/signal}]
              (if-let [created-ts (and delete-field?
                                       (:application/submitted target)
                                       (-> (du/find-descendant {:db/id form-id} target)
                                           :form/form-fields
                                           (p/find-first #(-> % :db/id (= form-field-id)))
                                           :form.field/entity-ref
                                           :entity/created))]
                (pos? (compare created-ts (:application/submitted target)))
                true))})

(def no-workspace-children
  "Ensure that there are no workspace-children of a given type"
  {:name    :process.guard/no-workspace-children
   :type    :guard
   :handler (fn [{{:keys [target]} :request
                  [_ type]         ::tk/guard}]
              (->> target
                   :entity/parent
                   (eu/descendants type)
                   empty?))})

(def no-parties-with-account
  "Ensure that there are no parties linked to a user account"
  {:name    :process.guard/no-parties-with-account
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (->> target
                   :entity/parent
                   :entity/_parent
                   (mapcat party-model/phase-parties)
                   (keep party-model/representee)
                   empty?))})


(def workspace-approval-authority-is
  {:name    :process.guard/workspace-approval-authority-is
   :type    :guard
   :handler (fn [{{:keys [target]}   :request
                  [_ authority-type] ::tk/guard}]
              (let [current-authority (->> target
                                           :entity/parent
                                           :workspace/approval-authority)]
                ;; Approval authority should be something only if it is not the same as the authority group
                (if (= authority-type :self)
                  (nil? current-authority)
                  (some? current-authority))))})


(def group-has-authority-delegates?
  {:name    :process.guard/group-has-authority-delegates?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (-> target
                  :entity/parent
                  :domain/authority-group
                  :group/authority-delegates
                  seq))})


(defn- user-auth-group-and-domain-ids [user]
  (let [auth-groups (authority/authority-groups user)]
    (->> auth-groups
         (mapcat (fn [ag]
                   (let [parent (:entity/parent ag)]
                     [(:db/id ag)
                      ;; cljs may not have parent in db, only its id
                      (or (:db/id parent) parent)])))
         set)))


(def user-is-committee-authority?
  {:name    :process.guard/user-is-committee-authority?
   :type    :guard
   :handler (fn [{[_ {:keys [committee-group-id]}] ::tk/signal
                  user                             :core.user/user}]
              (or (contains? (user-auth-group-and-domain-ids user) committee-group-id)
                  (guard-utils/user-is-admin? user)))})


(def ^:private workflow->subtype
  {:inspection.visit.type-workflow/occupancy-inspection :inspection.subtype/occupancy
   :inspection.visit.type-workflow/general-inspection   :inspection.subtype/general
   :inspection.visit.type-workflow/final-inspection     :inspection.subtype/general})

(defn- any-inspection-workflow-handler
  [{{:keys [target]} :request
    [_ workflow]     ::tk/guard}]
  (let [subtype (workflow->subtype workflow)]
    (-> (:entity/_parent target)
        (p/find-first (fn [entity]
                        (and (-> entity :entity/type (= :entity.type/inspection))
                             (or (-> entity :inspection/visit-type :inspection-visit-type/workflow (= workflow))
                                 (-> entity :inspection/subtype (= subtype)))
                             (-> entity :domain/state #{:inspection.state/active
                                                        :inspection.state/completed}))))
        some?)))

(def any-inspection-has-workflow?
  {:name    :process.guard/any-inspection-has-workflow?
   :type    :guard
   :handler any-inspection-workflow-handler})

(def no-inspection-has-workflow?
  {:name    :process.guard/no-inspection-has-workflow?
   :type    :guard
   :handler (complement any-inspection-workflow-handler)})


(def public-notice-contact-not-informed?
  {:name    :process.guard/public-notice-contact-not-informed?
   :type    :guard
   :handler (fn [{{:keys [target]}         :request
                  [_ {:keys [contact-id]}] ::tk/signal}]
              (->> (du/find-descendant {:db/id contact-id} (:entity/parent target))
                   :public-notice-contact/informed
                   nil?))})


(def created-by-user?
  {:name    :process.guard/created-by-user?
   :type    :guard
   :handler (fn [{user :core.user/user
                  :as  fsm}]
              (let [[_ entity-k]  (-> fsm :tilakone.core/guard)
                    target-entity (guard-utils/find-child-entity fsm entity-k)]
                (= (-> target-entity :entity/creator :db/id) (:db/id user))))})


(def current-circulation-round?
  {:name    :process.guard/current-circulation-round?
   :type    :guard
   :handler (fn [{{:keys [target]} :request
                  :as              fsm}]
              (let [ws                (:entity/parent target)
                    [_ entity-k]      (-> fsm :tilakone.core/guard)
                    target-entity     (guard-utils/find-child-entity fsm entity-k)
                    circulation-round (or (:workspace/circulation-round ws) 1)]
                (= (-> target-entity :checklist.comment/circulation-round) circulation-round)))})

(defn- can-respond-handler
  [{user :core.user/user
    :as  fsm}]
  (let [comment   (guard-utils/find-child-entity fsm :comment-id)
        responses (application-condition-model/comment-responses comment)]
    (if (seq responses)
      (let [latest-own  (some->> responses
                                 (filter #(= (-> % :entity/creator :db/id) (:db/id user)))
                                 seq
                                 eu/latest)
            latest-auth (some->> responses
                                 (filter :checklist.response/authority?)
                                 seq
                                 eu/latest)]
        (cond (and latest-own (not latest-auth)) false
              (and (not latest-own) (not latest-auth)) true
              (and (not latest-own) latest-auth) true
              (and latest-own latest-auth)
              (or (calendar/after (:entity/created latest-auth) (:entity/created latest-own))
                  (calendar/equals (:entity/created latest-auth) (:entity/created latest-own)))))
      true)))

(def can-respond-comment?
  "authority has responded after the users latest response
  or the user is authority, or there are no responses yet"
  {:name    :process.guard/can-respond-comment?
   :type    :guard
   :handler can-respond-handler})

(def can-edit-response?
  {:name    :process.guard/can-edit-response?
   :type    :guard
   :handler (complement can-respond-handler)})

(defn- permit-signed-handler [fsm]
  (verdict-model/verdicts-signed? (-> fsm :request :target)))

(def permit-signed?
  {:name    :process.guard/permit-signed?
   :type    :guard
   :handler permit-signed-handler})

(def permit-sign-off-enabled?
  {:name    :process.guard/permit-sign-off-enabled?
   :type    :guard
   :handler (fn [fsm]
              (->> fsm
                   :request
                   :target
                   verdict-model/application-verdicts
                   (filter :verdict/sign-off-required?)
                   seq))})

(def case-id-source-is?
  {:name    :process.guard/case-id-source-is?
   :type    :guard
   :handler (fn [{{:keys [target]}       :request
                  [_ & expected-sources] ::tk/guard}]
              (let [is-source-valid? (set expected-sources)]
                (-> target :entity/parent config-model/resolve-municipal-case-id-source-for-ws is-source-valid?)))})

(def pre-consultation-not-required?
  {:name    :process.guard/pre-consultation-not-required?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (-> target
                  :entity/parent
                  :workspace/category-group-config
                  :group.config/pre-consultation
                  (not= :group.config.pre-consultation/required)))})

(def phase-result-exists?
  {:name    :process.guard/phase-result-exists?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (some? (phase-result-model/get-result target)))})

(def phase-result-has-values?
  {:name    :process.guard/phase-result-has-values?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (let [result (phase-result-model/get-result target)]
                (and (some? (:phase-result/description result))
                     (some? (:phase-result/closure-category result)))))})

(def permit-type-enabled?
  {:name    :process.guard/permit-type-enabled?
   :type    :guard
   :handler (fn [{{:keys [target]} :request
                  [_ permit-type]  ::tk/guard}]
              (->> target
                   :entity/parent
                   :workspace/municipality
                   :scope/_municipality
                   (some #(-> % :scope/permit-type (= permit-type)))))})

(def workspace-permit-type-in?
  {:name    :process.guard/workspace-permit-type-in?
   :type    :guard
   :summary "Only allowed for workspaces with one of the given permit types"
   :handler (fn [{{:keys [target]} :request
                  [_ permit-types] ::tk/guard}]
              (contains? (set permit-types)
                         (-> target :entity/parent :workspace/permit-type)))})

(def preceding-reviews-finished?
  {:name    :process.guard/preceding-reviews-finished?
   :type    :guard
   :summary "Check that there are no reviews without approval before the given review if the review config demands this."
   :handler (fn [{{:keys [target]}        :request
                  [_ {:keys [review-id]}] ::tk/signal}]
              (let [all-reviews (review-model/application-reviews target)
                    review      (p/find-first all-reviews {:db/id review-id})]
                (or (-> review :review/review-config :review.config/start-after-previous-finished? not)
                    (-> review (review-model/previous-unfinished-application-reviews all-reviews) empty?))))})

(def issue-date-today-or-earlier?
  {:name    :process.guard/issue-date-today-or-earlier?
   :type    :guard
   :summary "Check that permit issue date is today or earlier."
   :handler #?(:clj (fn [fsm]
                      (let [issue-date (guard-utils/get-param fsm :verdict/issue-date)]
                        ;; Blank string is allowed for clearing the date
                        (or (and (string? issue-date)
                                 (str/blank? issue-date))
                            (and (inst? issue-date)
                                 (time/date-today-or-before issue-date))))))
   #?(:cljs (fn [_] true))})

(def manual-case-id-required?
  {:name    :process.guard/manual-case-id-required?
   :type    :guard
   :summary "Check if the authority is required (and allowed) to input the municipal case id"
   :handler (fn [{{:keys [target]} :request}]
              (let [auth-group (-> target :entity/parent :domain/authority-group)]
                (and (group-model/has-feature? auth-group :group.feature/manual-case-ids)
                     (= (-> target
                            :entity/parent
                            config-model/resolve-municipal-case-id-source-for-ws)
                        :case-id-source/manual))))})

(def manual-case-id-done?
  {:name    :process.guard/manual-case-id-done?
   :type    :guard
   :summary "Check that a manual municipal case id has either been provided or is not required"
   :handler (fn [{{:keys [target]} :request}]
              (let [workspace  (:entity/parent target)
                    auth-group (:domain/authority-group workspace)]
                (or (not (group-model/has-feature? auth-group :group.feature/manual-case-ids))
                    (not= (-> workspace
                              config-model/resolve-municipal-case-id-source-for-ws)
                          :case-id-source/manual)
                    (some? (:workspace/municipal-case-id workspace)))))})

(def rejection-previous-state?
  {:name    :process.guard/rejection-previous-state?
   :type    :guard
   :summary "Check if the application was in the given states before being rejected"
   :handler (fn [{{:keys [target]}    :request
                  [_ expected-states] ::tk/guard}]
              (contains? (set expected-states) (:application/rejection-previous-state target)))})

(def rejection-previous-state-not?
  {:name    :process.guard/rejection-previous-state-not?
   :type    :guard
   :summary (str "Check if the application was not in the given states before being rejected. "
                 "Also passes if the previous state was not recorded.")
   :handler (complement (:handler rejection-previous-state?))})

(def previous-state-in?
  {:name    :process.guard/previous-state-in?
   :type    :guard
   :summary "Check if the application was in the given states before being set to blocked"
   :handler (fn [{{:keys [target]}              :request
                  [_ state-key expected-states] ::tk/guard}]
              (contains? (set expected-states) (state-key target)))})

(def previous-state-not-in?
  {:name    :process.guard/previous-state-not-in?
   :type    :guard
   :summary (str "Check if the application was not in the given states before its current state. "
                 "Also passes if the previous state was not recorded.")
   :handler (complement (:handler previous-state-in?))})

(defn- is-admin-of-target-group-handler [fsm]
  (let [target-group (-> fsm :request :target group-model/get-authority-group)
        user         (-> fsm :core.user/user)]
    #?(:clj  (perm/permitted? target-group user :permission.group/admin)
       :cljs (or (guard-utils/user-is-admin? user)
                 (authority/admin-authority-in-group? user target-group)))))

(def is-admin-of-target-group?
  {:name    :process.guard/is-admin-of-target-group?
   :type    :guard
   :summary "Check if the user is an administrator of the target entity's authority group."
   :handler is-admin-of-target-group-handler})

(def has-any-required-authority-roles?
  {:name    :process.guard/has-any-required-authority-roles?
   :type    :guard
   :summary "Check if the user has at least one required authority role in the target entity's authority group.
             Allowed authority roles are passed in as a guard parameter (both scalar and vector accepted)."
   :handler (fn [{user                        :core.user/user
                  {:keys [target]}            :request
                  [_ allowed-authority-roles] ::tk/guard}]
              (let [allowed-authority-roles (if (seqable? allowed-authority-roles)
                                              allowed-authority-roles
                                              '(allowed-authority-roles))]
                (group-model/any-required-authority-role?
                  (group-model/get-authority-group target) user allowed-authority-roles)))})

(def user-is-property-manager?
  {:name    :process.guard/user-is-property-manager?
   :type    :guard
   :summary "Check if the user has property management role in any of their groups."
   :handler (fn [{user             :core.user/user
                  {:keys [target]} :request}]
              (-> target
                  :entity/parent
                  :domain/authority-group
                  (permission-resolver/permitted? user :permission.property/write)))})

(def can-create-admin-package?
  {:name    :process.guard/can-create-admin-package?
   :type    :guard
   :summary "Check if the user can create an admin package for the target entity.
             The user must be an admin of the target entity's authority group or have either review or reporting rule"
   :handler (fn [{user                           :core.user/user
                  {:keys [target feature-flags]} :request
                  :as                            fsm}]
              (or (is-admin-of-target-group-handler fsm)
                  (and (contains? feature-flags :feature-flag.id/reporting-content-packages)
                       (-> (group-model/get-authority-group target)
                           (group-model/any-required-authority-role? user [:group.role/manager :group.role/reporting])))))})

(defn- find-config [parent-config child-id]
  (-> parent-config
      config-model/child-configs
      (p/find-first {:db/id child-id})))

(defn- new-config-pre-con-setting-one-of [workspace {:keys [category-id]} pre-con-settings]
  (some-> (:workspace/permit-type-group-config workspace)
          (find-config category-id)
          :group.config/pre-consultation
          pre-con-settings))

(defn- workspace-has-pre-consultation? [{{{workspace :entity/parent :as target} :target} :request}]
  (or (eu/is-type? :entity.type/pre-consultation target)
      (workspace-model/own-or-prior-pre-consultation workspace)))

(def pre-con-can-be-added?
  {:name    :process.guard/pre-con-can-be-added?
   :type    :guard
   :handler (fn [{{{workspace :entity/parent} :target} :request
                  [_ new-ws-config-ids]                ::tk/signal
                  :as                                  fsm}]
              (and (not (workspace-has-pre-consultation? fsm))
                   (new-config-pre-con-setting-one-of workspace
                                                      new-ws-config-ids
                                                      #{:group.config.pre-consultation/required
                                                        :group.config.pre-consultation/optional})))})

(def pre-con-exists?
  {:name    :process.guard/pre-con-exists?
   :type    :guard
   :handler workspace-has-pre-consultation?})

(def pre-con-is-applicable?
  {:name    :process.guard/pre-con-is-applicable?
   :type    :guard
   :handler (fn [{{{workspace :entity/parent :as target} :target} :request}]
              (when-let [pre-con (or (and (eu/is-type? :entity.type/pre-consultation target) target)
                                     ;; This guard is used with the signals that set pre-consultation to not applicable
                                     ;; if the category etc. ws config changes. We do not check
                                     ;; :workspace/prior-pre-consultation because we do not want to invalidate a pre-con
                                     ;; in another workspace.
                                     (workspace-model/workspace-pre-consultation workspace))]
                (not (du/in-state? :pre-consultation.state/not-applicable pre-con))))})

(def pre-con-does-not-exist?
  {:name    :process.guard/pre-con-does-not-exist?
   :type    :guard
   :handler (complement workspace-has-pre-consultation?)})

(def pre-con-exists-or-not-required?
  {:name    :process.guard/pre-con-exists-or-not-required?
   :type    :guard
   :handler (fn [{{{workspace :entity/parent} :target} :request
                  [_ new-ws-config-ids]                ::tk/signal
                  :as                                  fsm}]
              (or (workspace-has-pre-consultation? fsm)
                  (not (new-config-pre-con-setting-one-of workspace
                                                          new-ws-config-ids
                                                          #{:group.config.pre-consultation/required}))))})

(defn- workspace-category-gc-would-change?
  [{{{workspace :entity/parent} :target} :request
    [_ {:keys [category-id]}]            ::tk/signal}]
  (and category-id
       (-> workspace :workspace/category-group-config :db/id (not= category-id))))

(def ws-category-changes?
  {:name    :process.guard/ws-category-changes?
   :type    :guard
   :handler workspace-category-gc-would-change?})

(def ws-category-does-not-change?
  {:name    :process.guard/ws-category-does-not-change?
   :type    :guard
   :handler (complement workspace-category-gc-would-change?)})

(defn- check-manual-inputs-for-target-time [target time-value-key]
  (let [workspace      (:entity/parent target)
        manual-inputs? (and (group-model/has-feature? (:domain/authority-group workspace)
                                                      :group.feature/target-processing-times)
                            (config-model/resolve-boolean :group.config/target-time-manual-inputs? workspace))]
    (if manual-inputs?
      (-> target time-value-key some?)
      true)))

(def manual-completed-inputs?
  {:name    :process.guard/manual-completed-inputs?
   :type    :guard
   :handler (fn [fsm]
              (check-manual-inputs-for-target-time (-> fsm :request :target)
                                                   :application/target-time-completed-value))})

(def manual-decided-inputs?
  {:name    :process.guard/manual-decided-inputs?
   :type    :guard
   :handler (fn [fsm]
              (check-manual-inputs-for-target-time (-> fsm :request :target)
                                                   :application/target-time-decided-value))})

(def no-open-circulation-approvals?
  {:name    :process.guard/no-open-circulation-approvals?
   :type    :guard
   :handler (fn [{{work-phase :target} :request}]
              (-> work-phase approval-model/open-approvals? not))})

(def execution-not-finished?
  {:name    :process.guard/execution-not-finished?
   :type    :guard
   :handler (fn [fsm]
              (let [execution (-> fsm :request :target :entity/parent workspace-model/workspace-execution)]
                (not (du/in-state? :execution.state/finished execution))))})

(def verdict-in-post-issue-state?
  {:name    :process.guard/verdict-in-post-issue-state?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (let [workspace (:entity/parent target)]
                (or (-> workspace
                        workspace-model/workspace-application
                        (verdict-model/verdict-in-state verdict-model/post-issue-states))
                    (:workspace/legacy-permit? workspace))))})

(def allow-workspace-creation?
  {:name    :process.guard/allow-workspace-creation?
   :type    :guard
   :handler (fn [{{:keys [target]} :request
                  :as              fsm}]
              (let [workspace  (:entity/parent target)
                    authority? (guard-utils/has-permission? fsm :permission.application/authority-submit)
                    configs    (workspace-model/default-config-path workspace)]
                (config-model/all-allow-workspace-creation? authority? configs)))})

(def require-property-or-location-for-submit?
  {:name    :process.guard/require-property-or-location-for-submit?
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (let [workspace (:entity/parent target)]
                (or (config-model/resolve-boolean :group.config/no-workspace-location workspace)
                    (-> workspace :workspace/primary-property some?)
                    (-> workspace :entity/location some?))))})

(def pre-con-is-current-or-does-not-exist?
  {:name    :process.guard/pre-con-is-current-or-does-not-exist
   :type    :guard
   :handler (fn [{{:keys [target]} :request}]
              (or (eu/is-type? :entity.type/pre-consultation target)
                  (-> (:entity/parent target)
                      workspace-model/own-or-prior-pre-consultation
                      nil?)))})

(def can-update-payer?
  {:name    :process.guard/can-update-payer?
   :type    :guard
   :summary "Only allow the user to set themselves or their business as the payer, unless an authority user."
   :handler (fn [{[_ {:keys [payer-id]}] ::tk/signal
                  user                   :core.user/user :as fsm}]
              (if payer-id
                (let [new-payer-party (guard-utils/find-child-entity fsm :payer-id)]
                  (or (guard-utils/user-is-admin? user)
                      (authority/authority-in-group? user (-> fsm find-workspace :domain/authority-group))
                      (party-model/user-can-act-as-party? user new-payer-party)))
                true))})

(defn- find-escrow-account [fsm]
  (let [account-id   (guard-utils/get-param fsm :account-id)
        get-accounts (child/child-entities-of-type-fn :entity.type/escrow-account)]
    (-> fsm
        guard-utils/get-domain-group
        get-accounts
        (p/find-first {:db/id        account-id
                       :domain/state :escrow-account.state/active}))))

(def escrow-account-in-domain-group?
  {:name    :process.guard/escrow-account-in-domain-group?
   :type    :guard
   :handler (fn [fsm]
              (let [domain-group (guard-utils/get-domain-group fsm)
                    account      (find-escrow-account fsm)]
                (and account
                     (= (:entity/parent account) domain-group))))})

(def sufficient-escrow-balance?
  {:name    :process.guard/sufficient-escrow-balance?
   :type    :guard
   :handler (fn [fsm]
              (let [bill    (guard-utils/find-child-entity fsm :bill-id)
                    account (find-escrow-account fsm)]
                (and bill
                     account
                     (<= (fee-model/bill-amount bill)
                         (* 100 (:escrow-account/balance account))))))})

(def pre-con-expiry-not-past?
  {:name    :process.guard/pre-con-expiry-not-past?
   :type    :guard
   :summary "If pre-con expiration was applicable, check that the expiry date is not passsed. Normally,
             the state of the pre-con / application is changed when pre-con expires. However, when pre-con
             expiry is no longer applicable due to e.g. the application being withdrawn, it may be necessary
             to check that the actual date that is still stored in the pre-con is not in the past."
   :handler (fn [{{:keys [target now]} :request}]
              (if-let [expiration-date (some-> target
                                               :entity/parent ;; workspace
                                               :workspace/pre-consultation-expires-on)]
                (calendar/before #?(:clj now
                                    :cljs (or now (js/Date.)))
                                 expiration-date)
                true))})

(defn- late-fee-insignificant?
  "Checks if a late fee is insignificant (doesn't exist or is waived)."
  [late-fee-type fee]
  (let [late-fee (late-fee-type fee)
        amount   (some-> late-fee :fee/amount)]
    (or (not late-fee)
        (fee-model/fee-waived? late-fee)
        (nil? amount))))

(defn- late-fee-has-only-insignificant-charges? [fee]
  (and (late-fee-insignificant? :fee/penalty-fee fee)
       (late-fee-insignificant? :fee/interest-fee fee)))

(def bill-has-no-penalty-or-interest-fees?
  {:name    :process.guard/bill-has-no-penalty-or-interest-fees?
   :type    :guard
   :summary "Check that the bill does not contain any penalty or interest fees, or all penalty/interest fees are waived. Exception: draft reversal bills (with :bill/previous-bill) are always allowed."
   :handler (fn [{{:keys [target]}      :request
                  [_ {:keys [bill-id]}] ::tk/signal}]
              (let [bill            (du/find-descendant {:db/id bill-id} target)
                    fees            (:bill/fees bill)
                    ;; Allow due-date updates on draft reversal bills since their penalty fees
                    ;; are copies from the original bill, not based on this bill's due date
                    draft-reversal? (fee-model/draft-reversal-bill? bill)]
                (or draft-reversal?
                    (every? late-fee-has-only-insignificant-charges? fees))))})

(def assignee-required
  {:name    :process.guard/assignee-required
   :type    :guard
   :summary "Checks that the work order includes an assignee."
   :handler (fn [fsm]
              (-> fsm
                  :request
                  :target
                  :entity/parent
                  :workspace/assignee
                  some?))})

(def ^:private bill-confirmation-timeout-ms
  "Bill confirmation is considered timed out if more than two hours have passed."
  (* 2 60 60 1000))

(def bill-confirmation-timeout?
  {:name    :process.guard/bill-confirmation-timeout?
   :type    :guard
   :handler (fn [{{:keys [now]} :request :as fsm}]
              (let [payment (-> fsm
                                (guard-utils/find-child-entity :bill-id)
                                fee-model/last-payment)]
                (if-some [paid-ts (when (du/in-state? :payment.state/pending-confirmation
                                                      payment)
                                    (:payment/paid-ts payment))]
                  (let [time-pending-ms #?(:clj (time/millis-between paid-ts now)
                                           :cljs (date-util/period (or now (js/Date.)) paid-ts))]
                    (> time-pending-ms bill-confirmation-timeout-ms))
                  false)))})

(defn- express-issuance-enabled? [fsm]
  (and (feature-flag-enabled? fsm :feature-flag.id/express-issuance)
       (config-model/resolve-express-issuance? (-> fsm :request :target :entity/parent))))

(def express-issuance?
  "Check that express issuance is enabled for the application's workspace config
   and that the express issuance feature flag is enabled."
  {:name    :process.guard/express-issuance?
   :type    :guard
   :handler express-issuance-enabled?})

(def not-express-issuance?
  "Negation of express-issuance? — used as a next-step-guard to hide states
   that are skipped during express issuance from the progress indicator."
  {:name    :process.guard/not-express-issuance?
   :type    :guard
   :handler (complement express-issuance-enabled?)})

(def guards
  (concat [feature-enabled?
           feature-not-enabled?
           cond-guard
           constantly-true
           constantly-false
           target-in-state?
           target-not-in-state?
           application-in-state?
           integration-sync-state-allow-sync?
           ecm-sync-state-allow-sync?
           workspace-package-sent-to-ecm?
           integration-sync-work-type-set?
           deposit-paid-or-not-required?
           required-fields-set?
           required-fields-except-affidavit-set?
           required-fields-except-sign-off-set?
           forms-confirmed-for-submit?
           required-sign-off-fields-set?
           attachment-requirements-are-fulfilled?
           integration-external-id
           workspace-not-sent-to-integration?
           single-verdict-allowed
           mandatory-permit-fields-set
           parent-permit-in-state
           acceptance-can-be-responded-to-guard
           changes-requested-for-form?
           required-fields-for-acceptance-target-filled?
           form-not-locked-when-signed-off?
           child-entity-required-permissions
           child-entity-required-write-permissions-granted?
           approval-assignee-or-write-authority?
           is-sign-off-applicable?
           sign-off?
           sign-off-required?
           sign-off-not-done?
           sign-off-done-or-not-required-after-submission-changes?
           sign-off-required-after-submission-changes?
           sign-off-not-required-after-submission-changes?
           sign-off-missing-from-required-roles
           sign-off-or-affidavit-missing
           sign-off-done-by-authority
           sign-off-done-by-non-authority
           sign-off-upload-only?
           sign-off-not-upload-only?
           sign-off-started?
           group-feature?
           group-feature-disabled?
           domain-group-feature?
           workspace-child-in-state
           lock-attrs-after-permit-issued
           lock-attrs-after-visit-started
           allow-attrs
           no-visits-ongoing
           visit-ongoing
           inspection-visit-ongoing
           inspection-visits-in-states
           require-permission-for-attr
           no-visits
           no-valid-bookings
           not-linked-to-order?
           signature-role-requirement-met?
           no-bills-in-state?
           some-bills-in-state?
           bills-paid-before-inspection-request
           bill-has-external-id?
           some-bill-integration-sendable?
           bill-has-payer?
           bill-has-no-child?
           workspace-has-flag?
           workspace-does-not-have-flag?
           target-has-flag?
           target-does-not-have-flag?
           target-or-sibling-has-flag?
           other-bills-paid?
           unpaid-upfront-or-deposit-bill-created?
           upfront-bill-paid-or-not-required?
           form-signed-state-is
           required-fields-for-form-filled?
           form-confirmation-needed?
           partially-submitted?
           not-partially-submitted?
           never-submitted?
           multi-phase-workspace?
           only-workspace-child?
           workspace-does-not-have-children?
           workspace-has-children?
           partial-occupancy-enabled?
           work-not-locked?
           work-set-not-prevented?
           not-last-work-type
           config-flag-enabled?
           config-flag-not-enabled?
           all-inspections-passed?
           all-inspections-passed-except?
           no-non-final-inspection-open-when-final-inspection?
           has-no-inspections?
           has-inspection?
           all-orders-cleared?
           can-create-draft-report?
           can-create-final-report?
           application-has-completed-approval-comments?
           application-has-council-decision?
           report-has-unsynced-approval-comments?
           sign-off-role-requirement-met?
           only-form-from-schema?
           group-role-requirement-met?
           bill-phase-one-of
           user-is-property-manager?
           archiving-enabled?
           archiving-fields-have-value
           ws-children-can-be-archived?
           sign-off-by-form-party?
           party-in-state?
           committee-meeting-item-active?
           locked-attributes-with-user?
           not-sign-off-related-form?
           not-party-form?
           fee-bondable?
           fee-bond-cancelable?
           no-bonded-fees-in-bill?
           bill-does-not-contain-refund?
           all-conditions-closed?
           no-overdue-conditions?
           overdue-conditions?
           has-execution-or-all-conditions-closed?
           condition-attachment-present?
           condition-not-closed?
           form-field-id-is
           not-removing-form-file-created-before-submission?
           workspace-approval-authority-is
           group-has-authority-delegates?
           user-is-committee-authority?
           no-workspace-children
           no-parties-with-account
           any-inspection-has-workflow?
           no-inspection-has-workflow?
           public-notice-contact-not-informed?
           created-by-user?
           current-circulation-round?
           can-respond-comment?
           can-edit-response?
           permit-signed?
           permit-signature-role-requirement-met?
           permit-sign-off-enabled?
           unpaid-pre-consultation-request-bill-created?
           pre-consultation-partially-submitted?
           pre-consultation-not-partially-submitted?
           case-id-source-is?
           pre-consultation-not-required?
           phase-result-exists?
           phase-result-has-values?
           permit-type-enabled?
           workspace-permit-type-in?
           preceding-reviews-finished?
           issue-date-today-or-earlier?
           manual-case-id-required?
           manual-case-id-done?
           rejection-previous-state?
           rejection-previous-state-not?
           previous-state-in?
           previous-state-not-in?
           is-admin-of-target-group?
           has-any-required-authority-roles?
           can-create-admin-package?
           pre-con-can-be-added?
           pre-con-exists-or-not-required?
           pre-con-exists?
           pre-con-is-applicable?
           pre-con-does-not-exist?
           ws-category-changes?
           ws-category-does-not-change?
           manual-completed-inputs?
           manual-decided-inputs?
           no-open-circulation-approvals?
           execution-not-finished?
           verdict-in-post-issue-state?
           allow-workspace-creation?
           require-property-or-location-for-submit?
           pre-con-is-current-or-does-not-exist?
           can-update-payer?
           no-pending-pre-con-meetings?
           sufficient-escrow-balance?
           escrow-account-in-domain-group?
           pre-con-expiry-not-past?
           bill-has-no-penalty-or-interest-fees?
           assignee-required
           bill-confirmation-timeout?
           express-issuance?
           not-express-issuance?]
          bond-guards/guards
          review-guards/guards
          states-guards/guards
          appeals-guards/guards
          related-guards/guards
          property-guards/guards
          inspection-guards/guards
          party-guards/guards
          licence-guards/guards
          bim-guards/guards
          periodic-inspection-guards/guards))
